<template>
  <div class="temp-car-payment-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('tempCarPayment.page.title')" :subtitle="$t('tempCarPayment.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：停车场区域选择 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-panel ds-role-tree">
          <div class="ds-role-tree__title">{{ $t('tempCarPayment.page.parkingAreaTitle') }}</div>
          <ul class="ds-role-tree__list">
            <li v-for="(item, index) in parkingAreas" :key="index" class="ds-role-tree__item"
              :class="{ 'is-active': conditions.paNum === item.num }" @click="handleSwitchParkingArea(item)">
              {{ item.num }}
            </li>
          </ul>
          <div v-if="!parkingAreas.length" class="ds-role-tree__empty">
            {{ $t('tempCarPayment.page.emptyArea') }}
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.carNum" clearable class="w-full"
                :placeholder="$t('tempCarPayment.carNumPlaceholder')" @keyup.enter.native="queryCarInoutMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.state" clearable class="w-full"
                :placeholder="$t('tempCarPayment.carStatusPlaceholder')">
                <el-option :label="$t('tempCarPayment.selectCarStatus')" :value="null" />
                <el-option value="100300" :label="$t('tempCarPayment.inStatus')" />
                <el-option value="100400" :label="$t('tempCarPayment.paidStatus')" />
                <el-option value="100500" :label="$t('tempCarPayment.outStatus')" />
                <el-option value="100600" :label="$t('tempCarPayment.repayStatus')" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('tempCarPayment.entryStartTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('tempCarPayment.entryEndTime')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryCarInoutMethod">
              {{ $t('tempCarPayment.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetCarInoutMethod">
              {{ $t('tempCarPayment.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="queryCarInoutMethod">
            <template #left>
              <span class="ds-panel__title">{{ $t('tempCarPayment.inParkingTitle') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="payments" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('tempCarPayment.page.empty')" @retry="queryCarInoutMethod">
            <el-table-column prop="inoutId" :label="$t('tempCarPayment.inoutId')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inoutId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('tempCarPayment.carStatus')" align="center" width="130">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="resolveCarStatus(scope.row.carInout).type"
                  :label="resolveCarStatus(scope.row.carInout).label" />
              </template>
            </el-table-column>
            <el-table-column prop="carNum" :label="$t('tempCarPayment.carNum')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="inTime" :label="$t('tempCarPayment.inTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="payTypeName" :label="$t('tempCarPayment.payType')" align="center" min-width="120" />
            <el-table-column prop="payCharge" :label="$t('tempCarPayment.payableAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.payCharge || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="realCharge" :label="$t('tempCarPayment.paidAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.realCharge || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('tempCarPayment.payTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="pagination.current" :size="pagination.size" :total="pagination.total"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listParkingAreas, getCarInoutPaymentList } from '@/api/car/tempCarPaymentApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'TempCarPaymentList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      parkingAreas: [],
      payments: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      conditions: {
        paNum: '',
        state: null,
        carNum: '',
        startTime: null,
        endTime: null,
        iotApiCode: 'listCarInoutPaymentBmoImpl'
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.loadParkingAreas()
  },
  methods: {
    async loadParkingAreas() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.communityId
        }
        const res = await listParkingAreas(params)
        this.parkingAreas = res.parkingAreas
        if (this.parkingAreas.length > 0) {
          this.handleSwitchParkingArea(this.parkingAreas[0])
        }
      } catch (error) {
        console.error('加载停车区域失败:', error)
        this.$message.error(this.$t('tempCarPayment.loadParkingAreaError'))
      }
    },

    handleSwitchParkingArea(parkingArea) {
      this.conditions.paNum = parkingArea.num
      this.pagination.current = 1
      this.queryCarInoutMethod()
    },

    async queryCarInoutMethod() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.conditions,
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: this.communityId
        }

        // 清理参数
        if (params.carNum) params.carNum = params.carNum.trim()
        if (params.startTime) params.startTime = this.formatDate(params.startTime)
        if (params.endTime) params.endTime = this.formatDate(params.endTime)

        const res = await getCarInoutPaymentList(params)
        this.payments = res.data
        this.pagination.total = res.total
      } catch (error) {
        console.error('查询失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('tempCarPayment.queryError')
        this.$message.error(this.$t('tempCarPayment.queryError'))
      } finally {
        this.loading = false
      }
    },

    resetCarInoutMethod() {
      this.conditions = {
        ...this.conditions,
        state: null,
        carNum: '',
        startTime: null,
        endTime: null
      }
      this.pagination.current = 1
      this.queryCarInoutMethod()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.queryCarInoutMethod()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this.queryCarInoutMethod()
    },

    formatDate(date) {
      if (!date) return null
      const d = new Date(date)
      return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`
    },

    resolveCarStatus(status) {
      const statusMap = {
        '3306': { type: 'primary', label: this.$t('tempCarPayment.inStatus') },
        '100400': { type: 'success', label: this.$t('tempCarPayment.paidStatus') },
        '100500': { type: 'muted', label: this.$t('tempCarPayment.outStatus') },
        '100600': { type: 'danger', label: this.$t('tempCarPayment.repayStatus') }
      }
      return statusMap[status] || { type: 'muted', label: status || '-' }
    }
  }
}
</script>

<style lang="scss" scoped>
.temp-car-payment-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-role-tree {
  padding: var(--ds-spacing-md) 0;

  &__title {
    padding: 0 var(--ds-spacing-lg) var(--ds-spacing-sm);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-600);
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__item {
    padding: 10px var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    text-align: center;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary-sub);
      border-left-color: var(--ds-primary);
      color: var(--ds-primary);
      font-weight: 600;
    }
  }

  &__empty {
    padding: var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
    text-align: center;
  }
}
</style>
